import React from 'react';
import { motion } from 'framer-motion';
import { Wrench, Settings, ArrowRight, Network, Clock, Headphones } from 'lucide-react';

const ServiceCard = ({ title, icon: Icon, description, images }) => {
  return (
    <motion.div
      className="bg-white bg-opacity-90 rounded-xl shadow-lg overflow-hidden relative"
      whileHover={{ scale: 1.02, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-4 sm:p-6">
        <div className="flex items-center mb-4">
          <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
          <h3 className="text-xl sm:text-2xl font-bold ml-3 sm:ml-4">{title}</h3>
        </div>
        <p className="text-sm sm:text-base text-gray-700 mb-4">{description}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {images.map((image, index) => (
            <motion.div
              key={index}
              className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-md"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
            >
              <img
                src={image}
                alt={`${title} ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const StrengthCard = ({ icon: Icon, title, description }) => (
  <motion.div
    className="bg-gradient-to-br from-blue-500 to-purple-600 p-4 sm:p-6 rounded-lg text-white"
    whileHover={{ scale: 1.05, rotate: 2 }}
    transition={{ duration: 0.3 }}
  >
    <Icon className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-yellow-300" />
    <h3 className="text-xl sm:text-2xl font-semibold mb-4 text-center">{title}</h3>
    <p className="text-sm sm:text-base text-center">{description}</p>
  </motion.div>
);

const InnovativeRepairServicesPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-purple-100 overflow-hidden relative">
      {/* パララックス背景 */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute bg-gradient-to-r from-blue-300 to-purple-300 rounded-full opacity-20"
            style={{
              width: Math.random() * 100 + 50,
              height: Math.random() * 100 + 50,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50],
              x: [0, Math.random() * 100 - 50],
              scale: [1, Math.random() + 0.5],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          />
        ))}
      </div>

      <main className="container mx-auto px-4 pt-16 sm:pt-20 md:pt-32 pb-16 relative z-10">
        <motion.h1
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"
        >
          信頼の修理仲介サービス
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-lg sm:text-xl md:text-2xl text-gray-700 text-center mb-12 max-w-3xl mx-auto"
        >
          お客様の大切な機械を、最適な修理専門業者へつなぎます。
          豊富なネットワークと経験で、効率的な修理プロセスをサポートします。
        </motion.p>

        <div className="grid gap-8 md:grid-cols-2 mb-16 sm:mb-24">
          <ServiceCard
            title="機械・機器の修理仲介"
            icon={Wrench}
            description="フライス盤、ボール盤、旋盤などの工作機械から、モーター巻き替えなどの専門的な修理分野まで、幅広い機械・機器の修理を最適な専門業者に仲介いたします。"
            images={["/images/service/hanyosenban.jpg", "/images/service/motor.jpg"]}
          />
          <ServiceCard
            title="電動・エア工具の修理仲介"
            icon={Settings}
            description="電動ドリル、グラインダー、エアコンプレッサーなど、さまざまな電動・エアツールの修理を専門業者に仲介。プロ用から一般向けまで幅広く対応します。"
            images={["/images/service/kyocera1.jpg", "/images/service/CLB-10.png"]}
          />
        </div>

        <motion.section
          className="mb-16 sm:mb-24"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8 sm:mb-12 text-center text-gray-800">私たちの強み</h2>
          <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
            <StrengthCard
              icon={Network}
              title="豊富な提携ネットワーク"
              description="全国の優秀な修理業者と提携。お客様のニーズに最適な修理サービスをご紹介します。"
            />
            <StrengthCard
              icon={Clock}
              title="迅速な対応"
              description="お問い合わせから最短24時間以内に適切な業者をマッチング。スピーディーな修理プロセスをサポートします。"
            />
            <StrengthCard
              icon={Headphones}
              title="きめ細やかなサポート"
              description="修理前の相談から修理後のフォローまで、専門スタッフが丁寧にサポートいたします。"
            />
          </div>
        </motion.section>

        <motion.section
          className="text-center bg-white bg-opacity-70 p-6 sm:p-12 rounded-2xl shadow-xl"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-gray-800">お問い合わせ</h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 mb-6 sm:mb-8 max-w-3xl mx-auto">
            修理のご相談、お見積りのご依頼は、お気軽にお問い合わせください。
            経験豊富なスタッフが丁寧にご対応いたします。
          </p>
          <motion.a
            href="/contact"
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg sm:text-xl font-semibold rounded-full hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            お問い合わせはこちら
            <ArrowRight className="ml-2 w-5 h-5 sm:w-6 sm:h-6" />
          </motion.a>
        </motion.section>
      </main>
    </div>
  );
};

export default InnovativeRepairServicesPage;